import { useEffect } from 'react';

export const calculateKeyboardOffset = (
  layoutViewportHeight: number,
  visualViewportHeight: number,
  visualViewportOffsetTop: number,
) => {
  const offset = Math.max(0, Math.round(
    layoutViewportHeight - visualViewportHeight - visualViewportOffsetTop,
  ));

  // Mobile browser chrome can change the visual viewport by a few pixels.
  // A software keyboard creates a substantially larger covered area.
  return offset >= 80 ? offset : 0;
};

const isBottomSearchFocused = () => (
  document.activeElement instanceof Element
  && Boolean(document.activeElement.closest('.bottom-bar .icon-container__search'))
);

const useMobileKeyboardOffset = () => {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;

    if (!viewport) return undefined;

    let focusFrame: number | undefined;

    const updateOffset = () => {
      root.style.setProperty(
        '--mobile-visual-viewport-height',
        `${Math.round(viewport.height)}px`,
      );

      const offset = isBottomSearchFocused()
        ? calculateKeyboardOffset(window.innerHeight, viewport.height, viewport.offsetTop)
        : 0;

      root.style.setProperty('--mobile-keyboard-offset', `${offset}px`);
    };

    const updateAfterFocus = () => {
      if (focusFrame !== undefined) window.cancelAnimationFrame(focusFrame);
      focusFrame = window.requestAnimationFrame(updateOffset);
    };

    viewport.addEventListener('resize', updateOffset);
    viewport.addEventListener('scroll', updateOffset);
    document.addEventListener('focusin', updateAfterFocus);
    document.addEventListener('focusout', updateAfterFocus);
    window.addEventListener('orientationchange', updateAfterFocus);
    updateOffset();

    return () => {
      if (focusFrame !== undefined) window.cancelAnimationFrame(focusFrame);
      viewport.removeEventListener('resize', updateOffset);
      viewport.removeEventListener('scroll', updateOffset);
      document.removeEventListener('focusin', updateAfterFocus);
      document.removeEventListener('focusout', updateAfterFocus);
      window.removeEventListener('orientationchange', updateAfterFocus);
      root.style.removeProperty('--mobile-keyboard-offset');
      root.style.removeProperty('--mobile-visual-viewport-height');
    };
  }, []);
};

export default useMobileKeyboardOffset;
